/**
 * WishlistContext Module
 * 
 * This module provides a context for managing the user's wishlist functionality.
 * It handles loading, adding, removing, and checking wishlist items through the Neo4j database service.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { neo4jService } from '../services/neo4jService';
import { toast } from '../misc/ToastContext';

// Create a context for wishlist functionality
const WishlistContext = createContext();

/**
 * Custom hook to use the wishlist context
 * @returns {Object} Wishlist context value
 * @throws {Error} If used outside of WishlistProvider
 */
export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

/**
 * WishlistProvider component
 * Provides wishlist state and functions to children components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]); // Stores wishlist items
  const [loading, setLoading] = useState(true); // Tracks loading state
  const { currentUser } = useAuth(); // Get current user from auth context

  /**
   * Loads the user's wishlist from the database
   * Sets empty wishlist if user is not logged in
   */
  const loadWishlist = async () => {
    if (!currentUser) {
      setWishlistItems([]);
      setLoading(false);
      return;
    }

    try {
      const items = await neo4jService.getWishlist(currentUser.id);
      setWishlistItems(items);
    } catch (error) {
      console.error('Error loading wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  // Load wishlist when user changes
  useEffect(() => {
    loadWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  /**
   * Adds a product to the user's wishlist
   * @param {Object} product - The product to add to wishlist
   * @returns {boolean} Success status
   */
  const addToWishlist = async (product) => {
    if (!currentUser) {
      toast.error('Please log in to add items to your wishlist');
      return false;
    }

    try {
      const success = await neo4jService.addToWishlist(currentUser.id, product.id);
      if (success) {
        setWishlistItems(prev => [...prev, product]);
        toast.success('Added to wishlist');
        return true;
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast.error('Failed to add to wishlist');
    }
    return false;
  };

  /**
   * Removes a product from the user's wishlist
   * @param {string} productId - ID of the product to remove
   * @returns {boolean} Success status
   */
  const removeFromWishlist = async (productId) => {
    if (!currentUser) return false;

    try {
      const success = await neo4jService.removeFromWishlist(currentUser.id, productId);
      if (success) {
        setWishlistItems(prev => prev.filter(item => item.id !== productId));
        toast.success('Removed from wishlist');
        return true;
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error('Failed to remove from wishlist');
    }
    return false;
  };

  /**
   * Checks if a product is in the user's wishlist
   * @param {string} productId - ID of the product to check
   * @returns {boolean} True if product is in wishlist
   */
  const isInWishlist = async (productId) => {
    if (!currentUser) return false;
    try {
      return await neo4jService.isProductWishlisted(currentUser.id, productId);
    } catch (error) {
      console.error('Error checking wishlist status:', error);
      return false;
    }
  };

  // Context value with wishlist state and functions
  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
